// Relative, not '@/': the API's test suite imports this module without Helm's aliases.
import type {
  CustomerAddress,
  CustomerAddressType,
  CustomerContact,
  CustomerPortalStatus,
} from '../../../../../lib/api/types'

export type CustomerEditField =
  | 'company_name'
  | 'contact_person'
  | 'customer_language'
  | 'default_currency'
  | 'email'
  | 'name'
  | 'notes'
  | 'phone'
  | 'portal_status'
  | 'social_profiles'
  | 'tin_number'
  | 'type'
  | 'website_url'
  | 'whatsapp_group_id'

export interface CustomerEditFieldError {
  code: string
  message: string
}

export type CustomerEditFieldErrors = Partial<Record<CustomerEditField, CustomerEditFieldError>>

export interface CustomerEditValues {
  name: string
  type: 'INDIVIDUAL' | 'BUSINESS'
  companyName: string
  contactPerson: string
  tinNumber: string
  phone: string
  email: string
  whatsappGroupId: string
}

const customerEditFields = new Set<CustomerEditField>([
  'company_name',
  'contact_person',
  'customer_language',
  'default_currency',
  'email',
  'name',
  'notes',
  'phone',
  'portal_status',
  'social_profiles',
  'tin_number',
  'type',
  'website_url',
  'whatsapp_group_id',
])

/** An API error on a list row, e.g. contacts.1.primary_email, under the editor's own key. */
function listErrorKey(path: string): string | null {
  const match = /^(contacts|addresses)\.(\d+)\.([a-z_0-9]+)/.exec(path)
  if (!match) return null
  const [, list, index, field] = match
  const names: Record<string, string> =
    list === 'contacts'
      ? { primary_email: 'email', primary_phone: 'phone' }
      : { raw_address: 'address' }
  return `${list === 'contacts' ? 'contact' : 'address'}-${index}-${names[field!] ?? field}`
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function errorMessage(error: unknown) {
  return error instanceof Error && error.message ? error.message : 'An error occurred while saving'
}

export function customerEditErrorsFromApi(error: unknown): {
  fields: CustomerEditFieldErrors
  /** Errors on contact and address rows, keyed as the list editors key them (request order). */
  lists: Record<string, string>
  form: string | null
} {
  const fields: CustomerEditFieldErrors = {}
  const lists: Record<string, string> = {}
  const details = isRecord(error) && isRecord(error.details) ? error.details : null
  const fieldErrors = details && isRecord(details.field_errors) ? details.field_errors : null

  if (fieldErrors) {
    for (const [field, value] of Object.entries(fieldErrors)) {
      if (!isRecord(value)) continue
      if (typeof value.code !== 'string' || typeof value.message !== 'string') continue
      const listKey = listErrorKey(field)
      if (listKey) {
        lists[listKey] = value.message
        continue
      }
      if (!customerEditFields.has(field as CustomerEditField)) continue
      fields[field as CustomerEditField] = {
        code: value.code,
        message: value.message,
      }
    }
  }

  const hasFields = Object.keys(fields).length > 0
  const hasLists = Object.keys(lists).length > 0
  return {
    fields,
    lists,
    form: hasFields
      ? null
      : hasLists
        ? 'Check the highlighted contacts and addresses'
        : errorMessage(error),
  }
}

export function validateCustomerEditValues(values: CustomerEditValues): CustomerEditFieldErrors {
  const errors: CustomerEditFieldErrors = {}

  if (!values.name.trim()) {
    errors.name = { code: 'REQUIRED', message: 'Name is required' }
  }
  if (values.type === 'BUSINESS' && !values.companyName.trim()) {
    errors.company_name = {
      code: 'REQUIRED',
      message: 'Company name is required for business customers',
    }
  }
  if (!values.tinNumber.trim()) {
    errors.tin_number = { code: 'REQUIRED', message: 'TIN number is required' }
  }
  if (!values.phone.trim()) {
    errors.phone = { code: 'REQUIRED', message: 'Phone is required' }
  }

  const email = values.email.trim()
  if (!email) {
    errors.email = { code: 'REQUIRED', message: 'Email is required' }
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = { code: 'INVALID_FORMAT', message: 'Enter a valid email address' }
  }

  return errors
}

export function buildCustomerUpdateRequest(values: CustomerEditValues) {
  const isBusiness = values.type === 'BUSINESS'

  return {
    name: values.name.trim(),
    type: values.type,
    company_name: isBusiness ? values.companyName.trim() || null : null,
    contact_person: isBusiness ? values.contactPerson.trim() || null : null,
    tin_number: values.tinNumber.trim(),
    phone: values.phone.trim(),
    email: values.email.trim(),
    whatsapp_group_id: values.whatsappGroupId.trim() || null,
  }
}

/** Everything the edit dialog shows apart from the lists: identity, invoicing, portal and online. */
export interface CustomerEditFormValues extends CustomerEditValues {
  defaultCurrency: string
  customerLanguage: string
  websiteUrl: string
  notes: string
  portalStatus: CustomerPortalStatus
  /** The stored profiles, every key kept, plus the ones the form offers (linkedin, x, instagram). */
  socialProfiles: Record<string, string>
}

/** The profiles the form offers; any other stored key is kept as it is. */
export const SOCIAL_PROFILE_KEYS = ['linkedin', 'x', 'instagram'] as const

export function socialProfileValues(stored: Record<string, string | null> | null | undefined) {
  const values: Record<string, string> = {}
  for (const [key, value] of Object.entries(stored ?? {})) values[key] = value ?? ''
  for (const key of SOCIAL_PROFILE_KEYS) values[key] ??= ''
  return values
}

/** Every editable field as the API stores it: trimmed, empty optional text as null. */
export function buildCustomerEditRequest(values: CustomerEditFormValues) {
  return {
    ...buildCustomerUpdateRequest(values),
    default_currency: values.defaultCurrency.trim().toUpperCase(),
    customer_language: values.customerLanguage.trim(),
    website_url: values.websiteUrl.trim() || null,
    notes: values.notes.trim() || null,
    portal_status: values.portalStatus,
    social_profiles: Object.fromEntries(
      Object.entries(values.socialProfiles).map(([key, value]) => [key, value.trim() || null]),
    ),
  }
}

export type CustomerEditRequest = ReturnType<typeof buildCustomerEditRequest>

/**
 * Only the fields the admin changed. Untouched fields stay out of the request,
 * so a placeholder the form hides stays as stored, the audit entry names what
 * really changed, and the email the customer signs in with is never resent.
 */
export function customerEditChanges(
  values: CustomerEditFormValues,
  initial: CustomerEditFormValues,
): Partial<CustomerEditRequest> {
  const next = buildCustomerEditRequest(values)
  const before = buildCustomerEditRequest(initial)
  return Object.fromEntries(
    Object.entries(next).filter(
      ([field, value]) =>
        JSON.stringify(before[field as keyof CustomerEditRequest]) !== JSON.stringify(value),
    ),
  ) as Partial<CustomerEditRequest>
}

/**
 * Errors for the fields being changed, so a legacy value nobody touched never
 * blocks a save. A business always needs its company name: the API refuses any
 * edit to a business without one.
 */
export function validateCustomerEdit(
  values: CustomerEditFormValues,
  initial: CustomerEditFormValues,
): CustomerEditFieldErrors {
  const errors: CustomerEditFieldErrors = validateCustomerEditValues(values)
  if (!/^[A-Z]{3}$/.test(values.defaultCurrency.trim().toUpperCase())) {
    errors.default_currency = {
      code: 'INVALID_FORMAT',
      message: 'Use a three-letter currency code, such as USD or UGX',
    }
  }
  if (!values.customerLanguage.trim()) {
    errors.customer_language = { code: 'REQUIRED', message: 'Language is required' }
  }
  const website = values.websiteUrl.trim()
  if (website && !/^https?:\/\/\S+\.\S+/i.test(website)) {
    errors.website_url = {
      code: 'INVALID_FORMAT',
      message: 'Enter the full link, starting with https://',
    }
  }

  const changed = customerEditChanges(values, initial)
  return Object.fromEntries(
    Object.entries(errors).filter(([field]) => field === 'company_name' || field in changed),
  ) as CustomerEditFieldErrors
}

export type ContactDraft = Omit<CustomerContact, 'id' | 'customer_id' | 'created_at' | 'updated_at'>
export type AddressDraft = Omit<CustomerAddress, 'id' | 'customer_id' | 'created_at' | 'updated_at'>

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** The stored contacts as editable rows, main contact first as the page lists them. */
export function contactDrafts(contacts: CustomerContact[] | null | undefined): ContactDraft[] {
  return (contacts ?? []).map(
    ({ id: _id, customer_id: _customer, created_at: _created, updated_at: _updated, ...rest }) => ({
      ...rest,
      emails: rest.emails ?? [],
      phones: rest.phones ?? [],
    }),
  )
}

export function addressDrafts(addresses: CustomerAddress[] | null | undefined): AddressDraft[] {
  return (addresses ?? []).map(
    ({ id: _id, customer_id: _customer, created_at: _created, updated_at: _updated, ...rest }) =>
      rest,
  )
}

export function blankContact(isPrimary: boolean): ContactDraft {
  return {
    name: '',
    title: null,
    department: null,
    primary_email: null,
    emails: [],
    primary_phone: null,
    phones: [],
    preferred_channel: 'EMAIL',
    is_primary: isPrimary,
    portal_access: false,
    notes: null,
  }
}

export function blankAddress(type: CustomerAddressType = 'SHIPPING'): AddressDraft {
  return {
    type,
    label: '',
    recipient_name: null,
    company_name: null,
    attention_to: null,
    phone: null,
    email: null,
    address_line1: null,
    address_line2: null,
    city: null,
    region: null,
    postal_code: null,
    country: null,
    raw_address: null,
    is_default_billing: false,
    is_default_shipping: false,
    notes: null,
    metadata: null,
  }
}

/** "a@x.test, b@x.test" or one per line, as a de-duplicated list. */
export function splitList(value: string) {
  return Array.from(
    new Set(
      value
        .split(/[,\n]/)
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  )
}

const text = (value: string | null | undefined) => value?.trim() || null

function contactIsEmpty(contact: ContactDraft) {
  return ![
    contact.name,
    contact.title,
    contact.department,
    contact.primary_email,
    contact.primary_phone,
    contact.notes,
    ...contact.emails,
    ...contact.phones,
  ].some((value) => value?.trim())
}

function addressIsEmpty(address: AddressDraft) {
  return ![
    address.label,
    address.recipient_name,
    address.company_name,
    address.attention_to,
    address.phone,
    address.email,
    address.address_line1,
    address.address_line2,
    address.city,
    address.region,
    address.postal_code,
    address.country,
    address.raw_address,
    address.notes,
  ].some((value) => value?.trim())
}

/**
 * The contacts as the API takes them, and which row each came from. Blank rows
 * are dropped; the first email and phone lead their lists; exactly one is the
 * main contact.
 */
export function buildContactsRequest(drafts: ContactDraft[]) {
  const rows = drafts
    .map((contact, index) => ({ contact, index }))
    .filter(({ contact }) => !contactIsEmpty(contact))
  const contacts = rows.map(({ contact }) => {
    const emails = Array.from(
      new Set([contact.primary_email, ...contact.emails].map(text).filter(Boolean) as string[]),
    )
    const phones = Array.from(
      new Set([contact.primary_phone, ...contact.phones].map(text).filter(Boolean) as string[]),
    )
    return {
      name: contact.name.trim(),
      title: text(contact.title),
      department: text(contact.department),
      primary_email: emails[0] ?? null,
      emails,
      primary_phone: phones[0] ?? null,
      phones,
      preferred_channel: contact.preferred_channel,
      is_primary: contact.is_primary,
      portal_access: contact.portal_access,
      notes: text(contact.notes),
    }
  })
  if (contacts.length > 0 && !contacts.some((contact) => contact.is_primary)) {
    contacts[0]!.is_primary = true
  }
  return { contacts, sourceRows: rows.map(({ index }) => index) }
}

/** The addresses as the API takes them, and which row each came from. Blank rows are dropped. */
export function buildAddressesRequest(drafts: AddressDraft[]) {
  const rows = drafts
    .map((address, index) => ({ address, index }))
    .filter(({ address }) => !addressIsEmpty(address))
  const addresses = rows.map(({ address }) => ({
    type: address.type,
    label: address.label.trim(),
    recipient_name: text(address.recipient_name),
    company_name: text(address.company_name),
    attention_to: text(address.attention_to),
    phone: text(address.phone),
    email: text(address.email),
    address_line1: text(address.address_line1),
    address_line2: text(address.address_line2),
    city: text(address.city),
    region: text(address.region),
    postal_code: text(address.postal_code),
    country: text(address.country),
    raw_address: text(address.raw_address),
    is_default_billing: address.is_default_billing,
    is_default_shipping: address.is_default_shipping,
    notes: text(address.notes),
    metadata: address.metadata ?? null,
  }))
  return { addresses, sourceRows: rows.map(({ index }) => index) }
}

/** Row errors, keyed contact-<row>-<field> and address-<row>-<field> as the editors show them. */
export function validateCustomerLists(contacts: ContactDraft[], addresses: AddressDraft[]) {
  const errors: Record<string, string> = {}
  contacts.forEach((contact, index) => {
    if (contactIsEmpty(contact)) return
    if (!contact.name.trim())
      errors[`contact-${index}-name`] = 'Add the name, or remove this contact'
    if (contact.primary_email?.trim() && !EMAIL.test(contact.primary_email.trim())) {
      errors[`contact-${index}-email`] = 'Enter a valid email address'
    }
    if (contact.emails.some((email) => !EMAIL.test(email))) {
      errors[`contact-${index}-emails`] = 'Separate valid email addresses with commas'
    }
  })
  addresses.forEach((address, index) => {
    if (addressIsEmpty(address)) return
    if (!address.label.trim()) errors[`address-${index}-label`] = 'Give the address a label'
    if (address.email?.trim() && !EMAIL.test(address.email.trim())) {
      errors[`address-${index}-email`] = 'Enter a valid email address'
    }
  })
  return errors
}

/** Whether a list differs from what was loaded, compared as it would be sent. */
export function listChanged<T>(build: (rows: T[]) => unknown, next: T[], initial: T[]) {
  return JSON.stringify(build(next)) !== JSON.stringify(build(initial))
}
